/**
 * LocationPicker - Modal/Sheet for selecting location
 * Uses unified MobileSheet component for consistent UX
 * Options: Current location, Enter address with autocomplete, Recent locations, Map selection
 */
import { useState, useCallback, useEffect, memo, useRef } from 'react';
import { MapPin, Navigation, Clock, Search, X, ChevronRight, Loader2, Crosshair } from 'lucide-react';
import { MobileSheet, MobileSheetHeader, MobileSheetContent } from '@/components/ui/mobile-sheet';
import { cn } from '@/lib/utils';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore - Fix missing icon URLs in bundled environment
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

export interface LocationData {
  label: string;
  lat?: number;
  lng?: number;
  source: 'geolocate' | 'manual' | 'history';
}

interface AutocompleteResult {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    road?: string;
    postcode?: string;
  };
}

// Restaurant marker data
interface RestaurantMarker {
  id: string | number;
  name: string;
  lat: number;
  lng: number;
}

interface LocationPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentLocation: LocationData | null;
  onLocationSelect: (location: LocationData) => void;
  restaurants?: RestaurantMarker[];
}

// Store recent locations in localStorage
const RECENT_LOCATIONS_KEY = 'speiseguru_recent_locations';
const MAX_RECENT_LOCATIONS = 3;

function getRecentLocations(): LocationData[] {
  try {
    const stored = localStorage.getItem(RECENT_LOCATIONS_KEY);
    if (stored) {
      return JSON.parse(stored).slice(0, MAX_RECENT_LOCATIONS);
    }
  } catch {
    // ignore
  }
  return [];
}

function addRecentLocation(location: LocationData) {
  try {
    const recent = getRecentLocations();
    const filtered = recent.filter(l => l.label.toLowerCase() !== location.label.toLowerCase());
    const updated = [location, ...filtered].slice(0, MAX_RECENT_LOCATIONS);
    localStorage.setItem(RECENT_LOCATIONS_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

// Format address from Nominatim result
function formatAddress(result: AutocompleteResult): string {
  const parts: string[] = [];
  const addr = result.address;
  
  if (addr) {
    if (addr.road) parts.push(addr.road);
    if (addr.city || addr.town || addr.village) {
      parts.push(addr.city || addr.town || addr.village || '');
    }
    if (addr.postcode && parts.length > 0) {
      const lastIdx = parts.length - 1;
      parts[lastIdx] = `${addr.postcode} ${parts[lastIdx]}`;
    }
  }
  
  if (parts.length === 0) {
    return result.display_name.split(',').slice(0, 2).join(',').trim();
  }
  
  return parts.join(', ');
}

export const LocationPicker = memo(function LocationPicker({
  open,
  onOpenChange,
  currentLocation,
  onLocationSelect,
  restaurants = []
}: LocationPickerProps) {
  const [addressInput, setAddressInput] = useState('');
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [recentLocations, setRecentLocations] = useState<LocationData[]>([]);
  
  // Map search state
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchResults, setMapSearchResults] = useState<AutocompleteResult[]>([]);
  const [isMapSearching, setIsMapSearching] = useState(false);
  const [showMapSearchResults, setShowMapSearchResults] = useState(false);
  const mapSearchDebounceRef = useRef<NodeJS.Timeout>();
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([51.1657, 10.4515]); // Germany center
  const [mapReady, setMapReady] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const restaurantMarkersRef = useRef<L.Marker[]>([]);

  // Load recent locations on open
  useEffect(() => {
    if (open) {
      setRecentLocations(getRecentLocations());
      setGeoError(null);
      setSuggestions([]);
      setAddressInput('');
      setMapReady(true);
      if (currentLocation?.lat && currentLocation?.lng) {
        setMapCenter([currentLocation.lat, currentLocation.lng]);
      }
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      setMapReady(false);
    }
  }, [open, currentLocation]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }
    
    const map = L.map(mapRef.current, {
      center: mapCenter,
      zoom: 13,
      zoomControl: true
    });
    
    // Use CartoDB Voyager tiles - more reliable than OSM default (no Referer issues)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
    }).addTo(map);
    
    const marker = L.marker(mapCenter, {
      draggable: true,
      autoPan: true
    }).addTo(map);
    
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      setMapCenter([pos.lat, pos.lng]);
    });
    
    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
      setMapCenter([e.latlng.lat, e.latlng.lng]);
    });
    
    const restaurantIcon = L.divIcon({
      className: 'restaurant-marker',
      html: '<div style="width:12px;height:12px;background:#009544;border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [12, 12],
      iconAnchor: [6, 6]
    });
    
    restaurantMarkersRef.current.forEach(m => m.remove());
    restaurantMarkersRef.current = [];
    
    restaurants.forEach(r => {
      if (r.lat && r.lng) {
        const rMarker = L.marker([r.lat, r.lng], { icon: restaurantIcon })
          .addTo(map)
          .bindPopup(`<b>${r.name}</b>`);
        restaurantMarkersRef.current.push(rMarker);
      }
    });
    
    if (restaurants.length > 0) {
      const allPoints: L.LatLngExpression[] = restaurants
        .filter(r => r.lat && r.lng)
        .map(r => [r.lat, r.lng] as L.LatLngExpression);
      allPoints.push(mapCenter);
      if (allPoints.length > 1) {
        map.fitBounds(L.latLngBounds(allPoints), { padding: [30, 30], maxZoom: 14 });
      }
    }
    
    leafletMapRef.current = map;
    markerRef.current = marker;
    
    setTimeout(() => map.invalidateSize(), 100);
    
    return () => {
      restaurantMarkersRef.current.forEach(m => m.remove());
      restaurantMarkersRef.current = [];
      map.remove();
      leafletMapRef.current = null;
      markerRef.current = null;
    };
  }, [mapReady, mapCenter[0], mapCenter[1], restaurants]);

  // Autocomplete search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    const query = addressInput.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=de&addressdetails=1`,
          { headers: { 'Accept-Language': 'de' } }
        );
        
        if (response.ok) {
          const results: AutocompleteResult[] = await response.json();
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [addressInput]);

  // Map search with debounce
  useEffect(() => {
    if (mapSearchDebounceRef.current) {
      clearTimeout(mapSearchDebounceRef.current);
    }
    
    const query = mapSearchQuery.trim();
    if (query.length < 2) {
      setMapSearchResults([]);
      setShowMapSearchResults(false);
      return;
    }
    
    mapSearchDebounceRef.current = setTimeout(async () => {
      setIsMapSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=de&addressdetails=1`,
          { headers: { 'Accept-Language': 'de' } }
        );
        
        if (response.ok) {
          const results: AutocompleteResult[] = await response.json();
          setMapSearchResults(results);
          setShowMapSearchResults(results.length > 0);
        }
      } catch {
        setMapSearchResults([]);
      } finally {
        setIsMapSearching(false);
      }
    }, 300);
    
    return () => {
      if (mapSearchDebounceRef.current) {
        clearTimeout(mapSearchDebounceRef.current);
      }
    };
  }, [mapSearchQuery]);

  const handleMapSearchResultClick = useCallback((result: AutocompleteResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setMapCenter([lat, lng]);
    setMapSearchQuery('');
    setShowMapSearchResults(false);
    
    if (leafletMapRef.current && markerRef.current) {
      leafletMapRef.current.setView([lat, lng], 15);
      markerRef.current.setLatLng([lat, lng]);
    }
  }, []);

  const handleGetCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setGeoError('Standortdienste nicht verfügbar');
      return;
    }

    setIsGeolocating(true);
    setGeoError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        
        let label = 'Aktueller Standort';
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
            { headers: { 'Accept-Language': 'de' } }
          );
          if (response.ok) {
            const data = await response.json();
            const addr = data.address;
            if (addr) {
              const parts = [];
              if (addr.road) parts.push(addr.road);
              if (addr.city || addr.town || addr.village) {
                parts.push(addr.city || addr.town || addr.village);
              }
              if (parts.length > 0) {
                label = parts.join(', ');
              }
            }
          }
        } catch {
          // Use default label
        }

        const location: LocationData = { label, lat, lng, source: 'geolocate' };
        addRecentLocation(location);
        onLocationSelect(location);
        setIsGeolocating(false);
        onOpenChange(false);
      },
      (error) => {
        setIsGeolocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGeoError('Standortzugriff verweigert');
            break;
          case error.POSITION_UNAVAILABLE:
            setGeoError('Standort nicht verfügbar');
            break;
          case error.TIMEOUT:
            setGeoError('Zeitüberschreitung');
            break;
          default:
            setGeoError('Standort konnte nicht ermittelt werden');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [onLocationSelect, onOpenChange]);

  const handleSuggestionClick = useCallback((result: AutocompleteResult) => {
    const location: LocationData = {
      label: formatAddress(result),
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      source: 'manual'
    };
    addRecentLocation(location);
    onLocationSelect(location);
    onOpenChange(false);
  }, [onLocationSelect, onOpenChange]);

  const handleAddressSubmit = useCallback(async () => {
    const trimmed = addressInput.trim();
    if (!trimmed) return;

    if (suggestions.length > 0) {
      handleSuggestionClick(suggestions[0]);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(trimmed)}&format=json&limit=1&countrycodes=de&addressdetails=1`,
        { headers: { 'Accept-Language': 'de' } }
      );
      
      if (response.ok) {
        const results: AutocompleteResult[] = await response.json();
        if (results.length > 0) {
          handleSuggestionClick(results[0]);
          return;
        }
      }
    } catch {
      // Fall through to manual entry
    }

    const location: LocationData = { label: trimmed, source: 'manual' };
    addRecentLocation(location);
    onLocationSelect(location);
    onOpenChange(false);
  }, [addressInput, suggestions, handleSuggestionClick, onLocationSelect, onOpenChange]);

  const handleRecentClick = useCallback((location: LocationData) => {
    onLocationSelect({ ...location, source: 'history' });
    onOpenChange(false);
  }, [onLocationSelect, onOpenChange]);

  const handleClearLocation = useCallback(() => {
    onLocationSelect({ label: '', source: 'manual' });
    onOpenChange(false);
  }, [onLocationSelect, onOpenChange]);

  const handleMapConfirm = useCallback(async () => {
    const [lat, lng] = mapCenter;
    
    let label = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
        { headers: { 'Accept-Language': 'de' } }
      );
      if (response.ok) {
        const data = await response.json();
        const addr = data.address;
        if (addr) {
          const parts = [];
          if (addr.road) parts.push(addr.road);
          if (addr.city || addr.town || addr.village) {
            parts.push(addr.city || addr.town || addr.village);
          }
          if (parts.length > 0) {
            label = parts.join(', ');
          }
        }
      }
    } catch {
      // Use coordinates as label
    }
    
    const location: LocationData = { label, lat, lng, source: 'manual' };
    addRecentLocation(location);
    onLocationSelect(location);
    onOpenChange(false);
  }, [mapCenter, onLocationSelect, onOpenChange]);

  return (
    <MobileSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Standort wählen"
      desktopMaxWidth="520px"
    >
      {/* Header */}
      <MobileSheetHeader onClose={() => onOpenChange(false)}>
        <h2 className="text-xl font-bold text-gray-900">Standort wählen</h2>
      </MobileSheetHeader>
      
      {/* Content */}
      <MobileSheetContent className="p-5 space-y-4">
        {/* Current Location Button */}
        <button
          onClick={handleGetCurrentLocation}
          disabled={isGeolocating}
          className={cn(
            "w-full flex items-center gap-3 p-4 rounded-xl border transition-colors",
            isGeolocating 
              ? "bg-gray-50 border-gray-200" 
              : "bg-[#E8F5EE] border-[#009544]/20 hover:bg-[#D0EBE0]"
          )}
        >
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center",
            isGeolocating ? "bg-gray-200" : "bg-[#009544]"
          )}>
            {isGeolocating ? (
              <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
            ) : (
              <Navigation className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="flex-1 text-left">
            <div className="font-medium text-gray-900">Aktuellen Standort verwenden</div>
            <div className="text-sm text-gray-500">GPS-Position ermitteln</div>
          </div>
          {!isGeolocating && <ChevronRight className="w-5 h-5 text-gray-400" />}
        </button>
        
        {geoError && (
          <p className="text-sm text-red-600 px-1">{geoError}</p>
        )}
        
        {/* Address Input with Autocomplete */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Adresse eingeben</label>
          <div className="relative">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={addressInput}
                  onChange={(e) => setAddressInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddressSubmit()}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Stadt, PLZ oder Straße..."
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#009544]/20 focus:border-[#009544]"
                  autoComplete="off"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
                {!isSearching && addressInput && (
                  <button
                    onClick={() => {
                      setAddressInput('');
                      setSuggestions([]);
                      setShowSuggestions(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Autocomplete Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-10"
                style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
              >
                {suggestions.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(result)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <MapPin className="w-4 h-4 text-[#009544] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {formatAddress(result)}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {result.display_name}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-400 px-1">
            Tippe eine Adresse ein und wähle aus den Vorschlägen
          </p>
        </div>
        
        {/* Recent Locations */}
        {recentLocations.length > 0 && !showSuggestions && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Clock className="w-4 h-4" />
              <span>Zuletzt verwendet</span>
            </div>
            <div className="space-y-1">
              {recentLocations.map((loc, i) => (
                <button
                  key={i}
                  onClick={() => handleRecentClick(loc)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                >
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{loc.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Map Selection */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Crosshair className="w-4 h-4 text-[#009544]" />
            <span>Standort auf Karte wählen</span>
          </div>
          
          {/* Map Search Box */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={mapSearchQuery}
                  onChange={(e) => setMapSearchQuery(e.target.value)}
                  onFocus={() => mapSearchResults.length > 0 && setShowMapSearchResults(true)}
                  placeholder="Auf Karte suchen..."
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#009544]/20 focus:border-[#009544]"
                  autoComplete="off"
                />
                {isMapSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
                {!isMapSearching && mapSearchQuery && (
                  <button
                    onClick={() => {
                      setMapSearchQuery('');
                      setMapSearchResults([]);
                      setShowMapSearchResults(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Map Search Results */}
            {showMapSearchResults && mapSearchResults.length > 0 && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-20"
                style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
              >
                {mapSearchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleMapSearchResultClick(result)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <MapPin className="w-4 h-4 text-[#009544] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {formatAddress(result)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div 
            ref={mapRef} 
            className="w-full h-[220px] rounded-xl overflow-hidden border border-gray-200"
            style={{ zIndex: 0 }}
          />
          <p className="text-xs text-gray-500 text-center">
            Tippe auf die Karte oder ziehe den Marker • Grüne Punkte = Restaurants
          </p>
          <button
            onClick={handleMapConfirm}
            className="w-full py-3 rounded-xl bg-[#009544] text-white font-medium hover:bg-[#007538] transition-colors"
          >
            Diesen Standort verwenden
          </button>
        </div>
        
        {/* Clear Location */}
        {currentLocation?.label && (
          <button
            onClick={handleClearLocation}
            className="w-full p-3 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Standort entfernen
          </button>
        )}
        
        {/* Bottom padding for safe area */}
        <div className="h-6" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
      </MobileSheetContent>
    </MobileSheet>
  );
});
